import { Modal, Box, IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faEnvelope, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { navigateExternal } from '../utils/navigation.js';
import "./behaviorUpgradeModal.css";

// Same shape + styling as KnowledgeUpgradeModal, but for the AI Studio limits:
// since the 2026-08 split, skills and behaviors each have their OWN allowance —
// the plan's `skill_limit` and `behavior_limit`. The modal opens for ONE
// artifact kind (whichever meter is full) and recommends the cheapest tier that
// raises THAT ceiling.
//
// The per-tier numbers aren't hardcoded here (unlike the knowledge modal): the
// limit values come from the live `plans` list (GET /subscription_plans),
// so the "recommended" badge always shows the real next-tier allowance.

// Fallback order used only to rank tiers when comparing the current plan to the
// candidates (the real limit always comes from the plan row itself).
const TIER_ORDER = ['Free', 'Smart', 'Boost', 'Ultimo'];

const tierRank = (name) => {
  const i = TIER_ORDER.indexOf(name);
  return i === -1 ? -1 : i;
};

// Which plan column caps the given artifact kind. A plan row that predates the
// split (or comes from a backend without the skill_limit column yet) may lack
// skill_limit — fall back to the old combined behavior_limit so the upgrade
// offer (bar CTA + modal ladder) never silently vanishes mid-rollout.
const LIMIT_FIELD = { skill: 'skill_limit', behaviour: 'behavior_limit' };
export const studioLimitField = (artifact) => LIMIT_FIELD[artifact] || 'skill_limit';
export const studioPlanLimit = (plan, artifact) =>
  (plan ? (plan[studioLimitField(artifact)] ?? plan.behavior_limit ?? 0) : 0);

// Pick the cheapest plan that (a) ranks above the current plan and (b) actually
// raises the ceiling for the given artifact kind above what the owner already
// uses. Returns null at the top. Exported so the Studio usage bar can hide its
// Upgrade CTA when there is nothing left to sell (the modal shows Contact Sales
// in that case).
export const getBestStudioPlan = (plans, currentPlanName, usedCount, artifact = 'skill') => {
  const currentIndex = tierRank(currentPlanName);
  const candidates = (plans || [])
    .filter((p) => tierRank(p.name) > currentIndex && studioPlanLimit(p, artifact) > usedCount)
    .sort((a, b) => tierRank(a.name) - tierRank(b.name));
  return candidates[0] || null;
};

const StudioUpgradeModal = ({ open, onClose, bot, plans, onUpgrade, artifact = 'skill', usedCount = 0, limit = 0, loading = false }) => {
  const { t } = useTranslation();

  if (!open) return null;

  const isBehaviour = artifact === 'behaviour';
  const currentPlanName = bot?.subscription_plan?.name || 'Free';
  const currentLimit = limit;
  const bestPlan = getBestStudioPlan(plans, currentPlanName, usedCount, artifact);
  const isHighestTier = !bestPlan;
  const countLabel = (n) => (isBehaviour ? t('studio_n_behaviours', { count: n }) : t('studio_n_skills', { count: n }));

  const handleContactSales = () => {
    navigateExternal('mailto:build@ultimo-bots.com?subject=Skills%20%26%20Behaviors%20Limit%20Inquiry');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outline: 'none',
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "20px",
          width: "100%",
          maxWidth: "500px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          outline: 'none',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
          aria-label={t('close')}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
        <div className="behavior-upgrade-modal-content">
          <div className="behavior-upgrade-modal-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <h2 className="behavior-upgrade-modal-title">
            {isBehaviour ? t('studio_behaviours_limit_reached_title') : t('studio_skills_limit_reached_title')}
          </h2>

          <p className="behavior-upgrade-modal-description">
            {isHighestTier
              ? (isBehaviour ? t('studio_behaviours_limit_highest_tier_desc') : t('studio_skills_limit_highest_tier_desc'))
              : (isBehaviour ? t('studio_behaviours_limit_upgrade_desc') : t('studio_skills_limit_upgrade_desc'))
            }
          </p>

          {isHighestTier ? (
            <button
              className="behavior-upgrade-button behavior-upgrade-contact-button"
              onClick={handleContactSales}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              {t('contact_sales')}
            </button>
          ) : (
            <>
              <div className="behavior-upgrade-plan-info">
                <div className="behavior-upgrade-plan-badge behavior-upgrade-current-plan">
                  <span className="behavior-upgrade-plan-label">{t('current_plan')}</span>
                  <span className="behavior-upgrade-plan-name">{currentPlanName}</span>
                  <span className="behavior-upgrade-plan-limit">
                    {countLabel(currentLimit)}
                  </span>
                </div>
                <div className="behavior-upgrade-arrow-icon">→</div>
                <div className="behavior-upgrade-plan-badge behavior-upgrade-next-plan">
                  <span className="behavior-upgrade-plan-label">{t('recommended')}</span>
                  <span className="behavior-upgrade-plan-name">{bestPlan.name}</span>
                  <span className="behavior-upgrade-plan-limit">
                    {countLabel(studioPlanLimit(bestPlan, artifact))}
                  </span>
                </div>
              </div>

              <button
                className="behavior-upgrade-button"
                onClick={onUpgrade}
                disabled={loading}
              >
                {loading
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> {t('redirecting')}</>
                  : t('upgrade_now')
                }
              </button>
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default StudioUpgradeModal;
